import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, Flame, Award, TrendingUp, Crown, Medal, Quote, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

// Motivational quotes for savings and financial goals
const MOTIVATIONAL_QUOTES = [
  { quote: "The secret to getting ahead is getting started.", author: "Mark Twain" },
  { quote: "A penny saved is a penny earned.", author: "Benjamin Franklin" },
  { quote: "Do not save what is left after spending; instead spend what is left after saving.", author: "Warren Buffett" },
  { quote: "Small amounts saved daily add up to huge investments in the end.", author: "Margo Vader" },
  { quote: "The habit of saving is itself an education.", author: "George S. Clason" },
  { quote: "It's not about how much money you make, but how much money you keep.", author: "Robert Kiyosaki" },
  { quote: "Financial freedom is available to those who learn about it and work for it.", author: "Robert Kiyosaki" },
  { quote: "Rich people stay rich by living like they're broke. Broke people stay broke by living like they're rich.", author: "Anonymous" },
  { quote: "The goal isn't more money. The goal is living life on your terms.", author: "Chris Brogan" },
  { quote: "Money is only a tool. It will take you wherever you wish, but it will not replace you as the driver.", author: "Ayn Rand" },
  { quote: "Wealth consists not in having great possessions, but in having few wants.", author: "Epictetus" },
  { quote: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
  { quote: "Every time you borrow money, you're robbing your future self.", author: "Nathan W. Morris" },
  { quote: "Savings is a great habit but without investment and preparation, it becomes futile.", author: "Margo Vader" },
  { quote: "Don't tell me where your priorities are. Show me where you spend your money and I'll tell you what they are.", author: "James W. Frick" },
  { quote: "Beware of little expenses. A small leak will sink a great ship.", author: "Benjamin Franklin" },
  { quote: "Money grows on the tree of persistence.", author: "Japanese Proverb" },
  { quote: "An investment in knowledge pays the best interest.", author: "Benjamin Franklin" },
  { quote: "The more you learn, the more you earn.", author: "Warren Buffett" },
  { quote: "Compound interest is the eighth wonder of the world.", author: "Albert Einstein" },
  { quote: "Never spend your money before you have earned it.", author: "Thomas Jefferson" },
  { quote: "A budget is telling your money where to go instead of wondering where it went.", author: "Dave Ramsey" },
  { quote: "Money saved today is freedom tomorrow.", author: "Anonymous" },
  { quote: "The journey of a thousand miles begins with a single step.", author: "Lao Tzu" },
  { quote: "Success is the sum of small efforts repeated day in and day out.", author: "Robert Collier" },
  { quote: "Discipline is the bridge between goals and accomplishment.", author: "Jim Rohn" },
  { quote: "Your future is created by what you do today, not tomorrow.", author: "Robert Kiyosaki" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Winners are not people who never fail, but people who never quit.", author: "Anonymous" },
  { quote: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
];

const Leaderboard = ({ user }) => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('streak');
  const [userRank, setUserRank] = useState(null);
  const [userStats, setUserStats] = useState(null);

  // Get daily quote based on date
  const dailyQuote = useMemo(() => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    return MOTIVATIONAL_QUOTES[dayOfYear % MOTIVATIONAL_QUOTES.length];
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [filter]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard?type=${filter}`);
      const data = response.data;
      setLeaderboard(data);
      
      // Find user's rank and stats
      const userIndex = data.findIndex(u => u.id === user.id);
      if (userIndex !== -1) {
        setUserRank(userIndex + 1);
        setUserStats(data[userIndex]);
      } else {
        setUserRank(null);
        setUserStats(null);
      }
    } catch (error) {
      toast.error('Failed to fetch leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-8 h-8 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-8 h-8 text-gray-400" />;
    if (rank === 3) return <Medal className="w-8 h-8 text-orange-600" />;
    return null;
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    if (rank <= 10) return "🌟";
    if (rank <= 50) return "⭐";
    return "💪";
  };

  const getStatDisplay = (userData) => {
    switch (filter) {
      case 'streak':
        return `${userData?.contribution_streak || 0} days`;
      case 'points':
        return `${userData?.reward_points || 0} pts`;
      case 'trust':
        return `${userData?.trust_score || 100}`;
      default:
        return '';
    }
  };

  const getMotivationalMessage = () => {
    if (!userRank) return "Start contributing to get on the leaderboard!";
    if (userRank === 1) return "You're the champion! Keep leading the way!";
    if (userRank <= 3) return "Amazing! You're in the top 3!";
    if (userRank <= 10) return "Great work! You're in the top 10!";
    if (userRank <= 50) return "Keep pushing! You're making great progress!";
    return "Every contribution counts! Keep going!";
  };

  const getFilterLabel = () => {
    switch (filter) {
      case 'streak': return 'Contribution Streak';
      case 'points': return 'Reward Points';
      case 'trust': return 'Trust Score';
      default: return '';
    }
  };

  const getFilterColor = () => {
    switch (filter) {
      case 'streak': return 'from-orange-500 to-red-500';
      case 'points': return 'from-purple-500 to-violet-500';
      case 'trust': return 'from-green-500 to-emerald-500';
      default: return 'from-blue-500 to-cyan-500';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* Header */}
      <div className="bg-card/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h1 className="text-4xl font-bold gradient-text mb-2" data-testid="leaderboard-title">
            Your Ranking
          </h1>
          <p className="text-muted-foreground">See where you stand in the SharePay community</p>
        </motion.div>

        {/* Filter Tabs */}
        <div className="flex justify-center gap-3 mb-8 flex-wrap">
          <Button
            onClick={() => setFilter('streak')}
            className={`rounded-full ${
              filter === 'streak'
                ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white'
                : 'bg-card text-foreground border border-border hover:bg-muted'
            }`}
            data-testid="filter-streak"
          >
            <Flame className="w-4 h-4 mr-2" />
            Streaks
          </Button>
          <Button
            onClick={() => setFilter('points')}
            className={`rounded-full ${
              filter === 'points'
                ? 'bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white'
                : 'bg-card text-foreground border border-border hover:bg-muted'
            }`}
            data-testid="filter-points"
          >
            <Award className="w-4 h-4 mr-2" />
            Points
          </Button>
          <Button
            onClick={() => setFilter('trust')}
            className={`rounded-full ${
              filter === 'trust'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white'
                : 'bg-card text-foreground border border-border hover:bg-muted'
            }`}
            data-testid="filter-trust"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Trust Score
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* User's Rank Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`rounded-3xl p-8 bg-gradient-to-br ${getFilterColor()} text-white shadow-2xl relative overflow-hidden`}
              data-testid="user-rank-card"
            >
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/30 -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-white/20 translate-y-1/2 -translate-x-1/2" />
              </div>

              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <p className="text-white/80 text-sm mb-1">{getFilterLabel()}</p>
                    <h2 className="text-2xl font-bold">{user.name}</h2>
                  </div>
                  {userRank && getRankIcon(userRank)}
                </div>

                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-6xl font-bold mb-1">
                      {userRank ? `#${userRank}` : '--'}
                    </p>
                    <p className="text-white/80 text-sm">Your Rank</p>
                  </div>
                  <div className="flex-1 text-center">
                    <p className="text-4xl font-bold mb-1">
                      {userStats ? getStatDisplay(userStats) : '--'}
                    </p>
                    <p className="text-white/80 text-sm">Your Score</p>
                  </div>
                  <div className="text-center">
                    <p className="text-4xl mb-1">{userRank ? getRankEmoji(userRank) : '🎯'}</p>
                    <p className="text-white/80 text-sm">Status</p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-white/10 rounded-xl backdrop-blur-sm">
                  <p className="text-center font-medium flex items-center justify-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    {getMotivationalMessage()}
                  </p>
                </div>

                {leaderboard.length > 0 && (
                  <p className="text-center text-white/70 text-sm mt-4">
                    Out of {leaderboard.length} active members
                  </p>
                )}
              </div>
            </motion.div>

            {/* Daily Motivational Quote */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl p-6 bg-card border border-border shadow-lg"
              data-testid="motivational-quote"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center shrink-0">
                  <Quote className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-lg font-medium text-foreground italic mb-2">
                    "{dailyQuote.quote}"
                  </p>
                  <p className="text-muted-foreground text-sm">
                    — {dailyQuote.author}
                  </p>
                  <p className="text-xs text-muted-foreground/60 mt-2">
                    Daily inspiration • Refreshes every day
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Quick Stats Summary */}
            {userStats && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-3 gap-4"
              >
                <div className="rounded-xl p-4 bg-card border border-border text-center">
                  <Flame className="w-6 h-6 text-orange-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold gradient-text">{userStats.contribution_streak || 0}</p>
                  <p className="text-xs text-muted-foreground">Day Streak</p>
                </div>
                <div className="rounded-xl p-4 bg-card border border-border text-center">
                  <Award className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold gradient-text">{userStats.reward_points || 0}</p>
                  <p className="text-xs text-muted-foreground">Points</p>
                </div>
                <div className="rounded-xl p-4 bg-card border border-border text-center">
                  <TrendingUp className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold gradient-text">{userStats.trust_score || 100}</p>
                  <p className="text-xs text-muted-foreground">Trust Score</p>
                </div>
              </motion.div>
            )}

            {/* Call to Action */}
            {!userRank && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-center py-6"
              >
                <p className="text-muted-foreground mb-4">
                  You're not on the leaderboard yet. Start contributing to your groups!
                </p>
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="rounded-full gradient-primary"
                >
                  Go to Dashboard
                </Button>
              </motion.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
